import { Flowbite, Button, Spinner } from "flowbite-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Route, Routes } from "react-router-dom";
import Loading from "./components/Loading";
import TitleBar from "./components/TitleBar";
import { getProtectedRoutes, getPublicRoutes } from "./config/routes";
import { AuthProvider } from "./contexts/AuthContext";
import { QueryClientContext } from "./contexts/QueryClientContext";
import NotFound from "./features/NotFound";
import RouteInspector from "./routes/RouteInspector";
import { flowbiteTheme } from "./theme";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { useAuth, useIncompleteIntegrationReminder } from "./hooks/helper";
import "./config/sentry";
import AnimatedModal from "./components/AnimatedModal";
import { httpService } from "./services/httpService";
import { queryClient } from "./contexts/QueryClientContext";
import axios from "axios";


const Layout = lazy(() => import("./components/Layout"));
import TawkChat from "./components/TawkChat";

function AppContent() {
  const { user, logout, loggingOut } = useAuth();
  const { i18n } = useTranslation();
  const publicRoutes = getPublicRoutes();
  const protectedRoutes = getProtectedRoutes(!!user?.is_superuser);

  // Enable incomplete integration reminder checker globally
  useIncompleteIntegrationReminder();

  // Session expired modal state
  const [showSessionModal, setShowSessionModal] = useState(false);
  const sessionDecisionResolve = useRef<
    ((v: "logout" | "continue") => void) | null
  >(null);

  // Register session prompt handler and dynamic import error listener
  useEffect(() => {
    // 1. Session handlers
    httpService.setSessionPromptHandler(() => {
      return new Promise<"logout" | "continue">((resolve) => {
        sessionDecisionResolve.current = resolve;
        setShowSessionModal(true);
      });
    });
    httpService.setLogoutHandler(() => logout());

    // 2. Dynamic import failure recovery
    // This catches "Failed to fetch dynamically imported module" which happens after new deployments
    // when the user's current session tries to load a deleted hashed asset.
    const handleGlobalError = (event: ErrorEvent | PromiseRejectionEvent) => {
      const errorMsg = "reason" in event ? event.reason?.message : event.message;
      if (
        errorMsg?.includes("Failed to fetch dynamically imported module") ||
        errorMsg?.includes("loading chunk") ||
        errorMsg?.includes("Expected a JavaScript-or-Wasm module script")
      ) {
        console.warn("Dynamic import failed, likely due to a new deployment. Reloading...", errorMsg);
        window.location.reload();
      }
    };

    window.addEventListener("error", handleGlobalError, true);
    window.addEventListener("unhandledrejection", handleGlobalError, true);

    // 3. Proactive deployment detection
    // Polling /meta.json to check for new versions
    let currentVersion: string | null = null;
    const checkVersion = async () => {
      try {
        const response = await fetch(`/meta.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return;
        const data = await response.json();

        if (!currentVersion) {
          currentVersion = data.version;
        } else if (currentVersion !== data.version) {
          console.log("New deployment detected. Reloading for latest updates...");
          window.location.reload();
        }
      } catch (err) {
        console.error("Failed to check version:", err);
      }
    };

    // Initial check and then every 5 minutes
    checkVersion();
    const versionInterval = setInterval(checkVersion, 5 * 60 * 1000);

    // 4. Prefetch device map once globally
    const prefetchDeviceMap = async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: ["device-map"],
          queryFn: async () => {
            const response = await axios.get("https://assets.apprevbooster.com/devices.json");
            return response.data;
          },
          staleTime: Infinity,
        });
      } catch (err) {
        console.error("Failed to prefetch device map:", err);
      }
    };
    prefetchDeviceMap();

    return () => {
      window.removeEventListener("error", handleGlobalError, true);
      window.removeEventListener("unhandledrejection", handleGlobalError, true);
      clearInterval(versionInterval);
    };
  }, [logout]);

  const handleSessionLogout = () => {
    logout();
    setShowSessionModal(false);
    sessionDecisionResolve.current = null;
  };

  const handleSessionContinue = () => {
    // Close modal and signal interceptor to refresh token
    setShowSessionModal(false);
    sessionDecisionResolve.current?.("continue");
  };
  return (
    <div dir={i18n.dir()} className="min-h-screen bg-gray-50">
      <TawkChat />
      <TitleBar />
      <Routes>
        {publicRoutes.map(({ path, element }) => (
          <Route
            key={path}
            element={<RouteInspector key={window.location.pathname} />}
          >
            <Route key={path} path={path} element={element} />
          </Route>
        ))}
        <Route element={<RouteInspector />}>
          {protectedRoutes.map(({ path, element }) => (
            <Route
              key={path}
              path={path}
              element={<Layout>{element}</Layout>}
            />
          ))}
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* Session Expired Modal */}
      <AnimatedModal
        show={showSessionModal}
        closeButton={false}
        onClose={() => {
          // If user dismisses, keep them on decision -> default to showing modal until action
          // We will not auto-resolve here to avoid ambiguous state
        }}
        title={i18n.t("common.session.expiredTitle")}
        bodyClassName="px-6 pt-4 pb-1"
        footer={
          <div className="flex items-center justify-center gap-3 w-full">
            <Button
              color="red"
              onClick={handleSessionLogout}
              isProcessing={loggingOut}
              processingSpinner={
                <Spinner size="sm" color="red" className="-mt-[2px]" />
              }
            >
              {i18n.t("common.session.logout")}
            </Button>
            <Button color="indigo" onClick={handleSessionContinue}>
              {i18n.t("common.session.continue")}
            </Button>
          </div>
        }
      >
        <div className="text-gray-700 text-center">
          {i18n.t("common.session.expiredDescription")}
        </div>
      </AnimatedModal>
      <Toaster
        position="bottom-center"
        reverseOrder={false}
        toastOptions={{
          style: {
            padding: "8px 12px",
            borderRadius: "8px",
            border: "none",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            fontSize: "14px",
            fontWeight: "500",
          },
          success: {
            style: {
              backgroundColor: "#10B981",
              color: "#FFFFFF",
            },
            iconTheme: {
              primary: "#FFFFFF",
              secondary: "#10B981",
            },
          },
          error: {
            style: {
              backgroundColor: "#EF4444",
              color: "#FFFFFF",
            },
            iconTheme: {
              primary: "#FFFFFF",
              secondary: "#EF4444",
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Flowbite theme={{ theme: flowbiteTheme }}>
        <QueryClientContext>
          <AuthProvider>
            <CurrencyProvider>
              {/* <NotificationProvider> */}
              <AppContent />
              {/* </NotificationProvider> */}
            </CurrencyProvider>
          </AuthProvider>
        </QueryClientContext>
      </Flowbite>
    </Suspense>
  );
}

export default App;
